import * as React from "react";
import { DialogContent, Grid, IconButton, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogActions } from "@mui/material";
import { getPropertyDoc, deletePropertyDoc, updateProperty } from "state/apis";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";

export default function PropertyDocument({ open, onClose, editData, editMode }) {
    const theme = useTheme();
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [data, setData] = React.useState([]);
    const [deleteItem, setDeleteItem] = React.useState(null);
    const [document, setDocument] = React.useState({
        docs: [],
    });

    React.useEffect(() => {
        const fetchData = async () => {
            if (editMode && editData) {
                try {
                    const res = await getPropertyDoc(editData.id);
                    setData(res?.data);
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            } else {
                setData([]);
            }
        };

        fetchData();
    }, [editData, editMode]);

    const handleAddDocument = () => {
        setDocument((prevData) => ({
            ...prevData,
            docs: [...prevData.docs, { name: "", file: null }],
        }));
    };

    const handleDocumentNameChange = (index, value) => {
        const updatedDocuments = [...document.docs];
        updatedDocuments[index].name = value;
        setDocument((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleCancelDocument = (indexToRemove) => {
        setDocument((prevState) => ({
            ...prevState,
            docs: prevState.docs.filter((_, index) => index !== indexToRemove),
        }));
    };
    const handleDocumentFileChange = (index, file) => {
        const updatedDocuments = [...document.docs];
        updatedDocuments[index].file = file;
        setDocument((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleDeleteClick = (row) => {
        setDeleteItem(row?.id);
        setDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmation = async () => {
        try {
            await deletePropertyDoc(deleteItem);
            toast.success("Property doc deleted successfully.");
            const res = await getPropertyDoc(editData.id);
            setData(res?.data);
            setDeleteConfirmationOpen(false);
        } catch (error) {
            console.error("Error deleting property doc:", error);
            toast.error("Failed to delete property doc. Please try again.");
        }
    };

    const handleSubmit = async () => {
        try {
            const response = await updateProperty(editData.id, document);
            toast.success(response.message);

            setDocument((prevData) => ({
                ...prevData,
                docs: [],
            }));
            const res = await getPropertyDoc(editData.id);
            setData(res?.data);
        } catch (error) {
            console.error("Error:", error);
            if (error.response) {
                toast.error(error.response.data.message);
            } else if (error.request) {
                toast.error("No response received from server.");
            } else {
                toast.error("An error occurred. Please try again.");
            }
        }
    };

    const handleDocumentOpen = (url) => {
        window.open(url, "_blank");
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
            <DialogTitle sx={{ textAlign: "center", fontWeight: "900", mb: "2px" }}>
                Property Documents
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} p={"5px"}></Grid>
                <TableContainer
                    sx={{ overflowX: "auto", marginBottom: theme.spacing(2), maxHeight: 900 }}
                >
                    <Table
                        stickyHeader
                        aria-label="sticky table"
                        sx={{ backgroundColor: "#fff" }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    fontWeight="900"
                                    sx={{
                                        backgroundColor: "#eef1f6",
                                        color: "#1a2138",
                                    }}
                                >
                                    Sr No
                                </TableCell>
                                <TableCell
                                    fontWeight="900"
                                    sx={{
                                        backgroundColor: "#eef1f6",
                                        color: "#1a2138",
                                    }}
                                >
                                    Doc name
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#eef1f6",
                                        color: "#5a6a85",
                                    }}
                                >
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length ? (
                                data
                                    .filter((row) => row?.isSold)
                                    .map((row, index) => (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell
                                                onClick={() => handleDocumentOpen(row.url)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                {row.name}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteClick(row)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        sx={{ textAlign: "center", fontWeight: "700", mb: "2px" }}
                                    >
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid item xs={12} m={2} border={1} p={2} sx={{ borderColor: "grey.400" }}>
                    <Grid container alignItems="center">
                        <Grid item xs={8}>
                            <label>Property Documents</label>
                        </Grid>
                        <Grid item xs={4} container justifyContent="flex-end">
                            <Button variant="contained" onClick={handleAddDocument}>
                                Add Document
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        {document?.docs?.map((document, index) => (
                            <Grid container spacing={2} key={index} p={1}>
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        color="primary"
                                        label="Document Name"
                                        value={document.name}
                                        onChange={(e) =>
                                            handleDocumentNameChange(index, e.target.value)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={5} margin={"auto"}>
                                    <input
                                        type="file"
                                        accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={(e) =>
                                            handleDocumentFileChange(index, e.target.files[0])
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2} container justify="end" margin="auto">
                                    <IconButton
                                        color="error"
                                        onClick={() => handleCancelDocument(index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <DialogActions>
                    {/* <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            color: theme.palette.primary,
                            backgroundColor: theme.palette.primary[800],
                        }}
                    >
                        Cancel
                    </Button> */}
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            color: theme.palette.primary.light,
                            backgroundColor: theme.palette.secondary[200],
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
                <Dialog
                    open={deleteConfirmationOpen}
                    onClose={() => setDeleteConfirmationOpen(false)}
                >
                    <DialogTitle fontWeight={"bold"}>Are you sure you want to delete?</DialogTitle>
                    <DialogActions>
                        <Button
                            onClick={() => setDeleteConfirmationOpen(false)}
                            sx={{ color: "#5a6a85" }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirmation} color="error" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
        </Dialog>
    );
}
